import React, { useEffect, useRef, useState } from 'react';
import { p } from '../i18n/useP';
import { usePersistentValueLocal } from '../hooks/usePersistentState';
import './StoryDeck.css';

/**
 * Gabarit unique des récits (« Résumé »).
 *
 * Un récit = une pile de chapitres aimantés, un rail de navigation qui se
 * déplie au survol, les flèches du clavier, et la mémoire du dernier chapitre
 * lu. Cette machinerie vivait en double dans PeopleStory et CcrStory : deux
 * copies du même défilement, du même observateur, du même rail — donc deux
 * endroits à corriger à chaque retouche. Un seul ici (2026-08-23).
 *
 * Chaque chapitre : { key, label, tone?, node }.
 *   `tone` : '' | 'tint' | 'deep' — le volet coloré de la diapositive.
 *   `numbered` : préfixe « 1 · Titre » en tête de slide. Une moitié de récit
 *     repart à 1, ce qu'un compteur CSS ne sait pas faire au milieu d'une liste.
 *   `storageKey` : mémorise le chapitre courant. On quitte un récit pour aller
 *     vérifier une donnée, on doit revenir où on était.
 *   `print` : rend le récit pour le PDF — UNE diapositive = UNE page A4. Le
 *     défilement aimanté, le rail et le clavier n'ont plus d'objet ; chaque
 *     chapitre devient une section de page (`ppl-print__section`, qui porte
 *     déjà le saut). Les chapitres restent construits au MÊME endroit : le
 *     récit imprimé ne peut pas diverger de celui qu'on lit à l'écran.
 */
export default function StoryDeck({ chapters, numbered = false, storageKey = null, ready = true, print = false }) {
  const [active, setActive] = useState(0);
  const [remembered, setRemembered] = usePersistentValueLocal(storageKey || '__story_none__', 0);
  const deckRef = useRef(null);
  const restored = useRef(false);

  const goTo = (i) => {
    const deck = deckRef.current;
    const s = deck && deck.querySelectorAll('.pst-slide')[i];
    if (s) deck.scrollTo({ top: s.offsetTop, behavior: 'smooth' });
  };

  /* Flèches du clavier, sans exiger d'avoir cliqué dans le cadre. Neutralisées
     dès que le focus est dans un champ : sinon on volerait les flèches à un
     formulaire ouvert ailleurs sur la page.
     Le chapitre courant se lit sur la POSITION DE DÉFILEMENT, pas sur l'état
     React : au premier affichage rien n'a encore été observé, et s'y fier
     laissait la toute première flèche sans effet. */
  useEffect(() => {
    const deck = deckRef.current;
    if (!deck || !ready) return undefined;
    const onKey = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const el = document.activeElement;
      const tag = el && el.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (el && el.isContentEditable)) return;
      const slides = Array.from(deck.querySelectorAll('.pst-slide'));
      if (!slides.length) return;
      let cur = 0;
      slides.forEach((sl, i) => { if (sl.offsetTop <= deck.scrollTop + 4) cur = i; });
      let to = null;
      if (e.key === 'ArrowDown' || e.key === 'PageDown') to = Math.min(slides.length - 1, cur + 1);
      else if (e.key === 'ArrowUp' || e.key === 'PageUp') to = Math.max(0, cur - 1);
      else if (e.key === 'Home') to = 0;
      else if (e.key === 'End') to = slides.length - 1;
      if (to === null || to === cur) return;
      e.preventDefault();
      deck.scrollTo({ top: slides[to].offsetTop, behavior: 'smooth' });
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [ready]);

  /* Rail de progression. Sans IntersectionObserver, seul le SURLIGNAGE se
     perd : la lecture, elle, marche. Un récit entier ne doit pas tomber pour
     un repère de navigation. */
  useEffect(() => {
    const deck = deckRef.current;
    if (!deck || !ready || !window.IntersectionObserver) return undefined;
    const slides = Array.from(deck.querySelectorAll('.pst-slide'));
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting && e.intersectionRatio > 0.5) {
          const i = slides.indexOf(e.target);
          setActive(i);
          // On ne mémorise qu'APRÈS restauration : sinon le passage par la
          // slide 1 au montage écraserait le chapitre qu'on voulait retrouver.
          if (storageKey && restored.current) setRemembered(i);
        }
      });
    }, { root: deck, threshold: [0, 0.5, 1] });
    slides.forEach((s) => io.observe(s));
    return () => io.disconnect();
  }, [ready, chapters.length]);   // eslint-disable-line

  // Retour au chapitre laissé, une seule fois, sans animation.
  useEffect(() => {
    const deck = deckRef.current;
    if (!deck || !ready || !storageKey || restored.current) return;
    const slides = deck.querySelectorAll('.pst-slide');
    if (!slides.length) return;
    const i = Math.min(Number(remembered) || 0, slides.length - 1);
    if (i > 0) deck.scrollTo({ top: slides[i].offsetTop, behavior: 'auto' });
    setActive(i);
    restored.current = true;
  }, [ready, chapters.length]);   // eslint-disable-line

  /* Rendu imprimé — après tous les hooks (jamais de hook conditionnel). Le deck
     n'étant pas monté, `deckRef` reste nul et les effets ci-dessus sortent
     d'eux-mêmes : rien à désactiver. */
  if (print) {
    return (
      <div className="pst pst--print">
        {chapters.map((c, i) => (
          <section key={c.key}
                   className={`ppl-print__section pst-slide${c.tone ? ` pst-slide--${c.tone}` : ''}`}>
            {numbered && <div className="pst-chap"><b>{i + 1}</b> · {c.label}</div>}
            {c.node}
          </section>
        ))}
      </div>
    );
  }

  return (
    <div className="pst">
      {/* L'indication vit AU-DESSUS du cadre : sous lui, elle prenait une ligne
          de la hauteur utile et se lisait après coup, quand il est trop tard
          pour apprendre à parcourir. */}
      <p className="pst-hint" aria-hidden="true">{p('↑ ↓ pour parcourir')}</p>
      {/* Le rail dit OÙ l'on est sans dire ce qu'il y a ailleurs : une colonne
          de pastilles au repos, un menu au survol ou au focus clavier. */}
      <nav className="pst-rail" aria-label={p('Chapitres')}>
        {chapters.map((c, i) => (
          <button key={c.key} type="button" className={active === i ? 'on' : ''}
                  aria-current={active === i ? 'true' : undefined}
                  onClick={() => goTo(i)}>
            <i aria-hidden="true" />
            <span className="pst-rail__n" aria-hidden="true">{i + 1}</span>
            <span className="pst-rail__lb">{c.label}</span>
          </button>
        ))}
      </nav>

      <div className="pst-deck" ref={deckRef} tabIndex={0}
           role="region" aria-label={p('Récit — flèches haut et bas pour changer de chapitre')}>
        {chapters.map((c, i) => (
          <section key={c.key} className={`pst-slide${c.tone ? ` pst-slide--${c.tone}` : ''}`}>
            {numbered && <div className="pst-chap"><b>{i + 1}</b> · {c.label}</div>}
            {c.node}
          </section>
        ))}
      </div>
    </div>
  );
}
